import React, { createContext, useContext, useEffect, useState } from 'react';
import { withTimeout } from '../lib/supabase';
import { waitForInitialSession, COMPANY_FETCH_TIMEOUT_MS } from '../lib/auth-session';
import { useSession } from '../hooks/useSession';
import { posthog } from '../lib/posthog';
import { getSelectedCompanyId, setSelectedCompanyId } from '../lib/active-company';
import { getApiUrl } from '../lib/api-config';

const COMPANY_CACHE_KEY = 'userCompanyData';

type CachedCompanyData = {
  companyId: string;
  userRole: 'admin' | 'member' | 'client' | 'vendor';
  isActiveCompany: boolean | null;
};

function readCompanyCache(): CachedCompanyData | null {
  try {
    const raw = localStorage.getItem(COMPANY_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedCompanyData) : null;
  } catch {
    return null;
  }
}

/** One company the person belongs to, with the role they hold THERE (roles are independent per company). */
export interface CompanyMembership {
  company_id: string;
  name: string;
  role: 'admin' | 'member' | 'client' | 'vendor';
  is_default: boolean;
  /** the company has no address yet (the app asks an admin for it) */
  address_missing: boolean;
}

/** The GLOBAL kind of the account (users.account_type): it picks the experience shown. It is not the role (one per company) and it authorizes nothing -- the backend and RLS do. */
export type AccountType = 'main' | 'client_account' | 'vendor_account';

interface UserContextType {
  user: any;
  // null until /api/me answers (or when the account has no type); accountTypeResolved tells "still unknown" apart from "answered".
  accountType: AccountType | null;
  accountTypeResolved: boolean;
  // Every company the person belongs to. Empty until loaded (and when the list cannot be read): the interface then simply shows a single company.
  companies: CompanyMembership[];
  // Works in another of the person's companies. Reloads the app so no data of the previous company stays on screen.
  switchCompany: (companyId: string) => void;
  companyId: string | null;
  userRole: 'admin' | 'member' | 'client' | 'vendor' | null;
  isActiveCompany: boolean | null;
  // null while unknown/not yet resolved (still loading, or no company at
  // all yet); true once we know the company's address is empty -- used by
  // App.tsx's route guard to force the user into Settings > Company until
  // it's filled in. See phase60_address_optional_and_min_one_admin.sql.
  companyAddressMissing: boolean | null;
  isLoading: boolean;
  // Set only when fetchUserCompanyRelation() timed out AND there was no
  // localStorage fallback to use instead -- see that function's own docs.
  // App.tsx shows this as an explicit error+Retry state instead of an
  // indefinite spinner.
  companyFetchError: string | null;
  refreshUser: () => Promise<void>;
  clearUser: () => void;
}

const UserContext = createContext<UserContextType>({
  user: null,
  accountType: null,
  accountTypeResolved: false,
  companies: [],
  switchCompany: () => {},
  companyId: null,
  userRole: null,
  isActiveCompany: null,
  companyAddressMissing: null,
  isLoading: true,
  companyFetchError: null,
  refreshUser: async () => {},
  clearUser: () => {},
});

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Root-cause fix (2026-09-20): `user` used to be local state fed by this
  // component's OWN onAuthStateChange subscription (one of three
  // independent subscriptions across the app). It now derives from the
  // single shared session cache (lib/auth-session.ts) instead.
  const session = useSession();
  const user = session?.user ?? null;
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'member' | 'client' | 'vendor' | null>(null);
  const [isActiveCompany, setIsActiveCompany] = useState<boolean | null>(null);
  const [companyAddressMissing, setCompanyAddressMissing] = useState<boolean | null>(null);
  const [companies, setCompanies] = useState<CompanyMembership[]>([]);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [accountTypeResolved, setAccountTypeResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyFetchError, setCompanyFetchError] = useState<string | null>(null);
  // True once the initial session has been seeded one way or another --
  // gates the company-fetch effect below so it doesn't run with the
  // default `user === null` before the real check has happened.
  const [authChecked, setAuthChecked] = useState(false);

  // Always resolves company/role from a live query -- a user whose
  // membership was deactivated or deleted must lose access the next time
  // the app loads, not keep whatever was last cached in localStorage.
  // localStorage is only ever a write-through cache of the last known-good
  // result, never the source of truth for an access decision.
  //
  // Root-cause fix (2026-09-20): this query used to have no timeout, and it
  // gates `isLoading`, which gates the ENTIRE authenticated app shell in
  // App.tsx -- a hung/slow query here used to leave the whole app stuck on
  // "Loading..." forever, not just this one piece of data. It's now bounded
  // by COMPANY_FETCH_TIMEOUT_MS. On a genuine "no active relation" result
  // (the `error || !data` branch, an immediate answer from Supabase, not a
  // timeout) the behavior is unchanged. Only a timeout/thrown network error
  // falls back to the last-known-good localStorage cache -- a slow
  // connection is not evidence the user lost access, so it must never be
  // treated the same as a confirmed "no company" result. `isBackgroundRetry`
  // marks the single silent reconciliation attempt fired after a
  // cache-fallback, so it doesn't re-toggle isLoading or escalate to the
  // visible error state if it fails too.
  const fetchUserCompanyRelation = async (userId: string, options: { isBackgroundRetry?: boolean } = {}): Promise<void> => {
    const { isBackgroundRetry = false } = options;
    if (!isBackgroundRetry) {
      setCompanyFetchError(null);
    }
    try {
      // The memberships come from the API (GET /api/me/companies): the browser never queries the tables itself. The company the person chose (several
      // memberships) or, without a choice, their default one.
      const chosenCompany = getSelectedCompanyId();
      const query = (async () => {
        const response = await fetch(getApiUrl('api/me/companies'));
        if (!response.ok) throw new Error(`api/me/companies answered ${response.status}`);
        const body = await response.json();
        const list: CompanyMembership[] = Array.isArray(body?.data) ? body.data : [];
        const pick = chosenCompany ? list.find((c) => c.company_id === chosenCompany) : list.find((c) => c.is_default);
        return {
          data: pick ? { company_id: pick.company_id, role: pick.role, is_active_company: pick.is_default, address_missing: pick.address_missing } : null,
          error: null as unknown,
        };
      })();
      const { data, error } = await withTimeout(
        query,
        COMPANY_FETCH_TIMEOUT_MS,
        'fetchUserCompanyRelation timed out'
      );

      if ((error || !data) && chosenCompany) {
        // The chosen company is no longer one of theirs (membership closed): forget the choice and use the default one.
        setSelectedCompanyId(null);
        return fetchUserCompanyRelation(userId, options);
      }

      if (error || !data) {
        console.warn('⚠️ No active company relation found for user:', userId, error);
        setCompanyId(null);
        setUserRole(null);
        setIsActiveCompany(null);
        setCompanyAddressMissing(null);
        setCompanyFetchError(null);
        localStorage.removeItem(COMPANY_CACHE_KEY);
        if (user) identifyUserWithPostHog(user);
        return;
      }

      const userCompanyData: CachedCompanyData = {
        companyId: data.company_id,
        userRole: data.role,
        isActiveCompany: data.is_active_company
      };
      localStorage.setItem(COMPANY_CACHE_KEY, JSON.stringify(userCompanyData));

      setCompanyId(data.company_id);
      setUserRole(data.role);
      setIsActiveCompany(data.is_active_company);
      setCompanyAddressMissing(data.address_missing);
      setCompanyFetchError(null);

      if (user) {
        const posthogCompanyData = {
          company_id: data.company_id,
          role: data.role,
          is_active_company: data.is_active_company,
          invited: false,
          id: 'live-' + data.company_id,
          user_id: user.id
        };
        identifyUserWithPostHog(user, posthogCompanyData);
      }
    } catch (err) {
      console.error('❌ Error/timeout fetching user company relation:', err);

      if (isBackgroundRetry) {
        // The single silent reconciliation attempt failed too -- leave
        // whatever the cache-fallback already put on screen alone, don't
        // escalate to the visible error state or retry again.
        return;
      }

      const cached = readCompanyCache();
      if (cached) {
        setCompanyId(cached.companyId);
        setUserRole(cached.userRole);
        setIsActiveCompany(cached.isActiveCompany);
        setCompanyFetchError(null);
        // Not a retry loop -- exactly one follow-up attempt in the
        // background, in case the slow query would eventually have
        // returned different data (e.g. a role change or deactivation
        // that happened right as this timed out). Never logs out and
        // never blocks the UI while it runs.
        fetchUserCompanyRelation(userId, { isBackgroundRetry: true });
      } else {
        setCompanyId(null);
        setUserRole(null);
        setIsActiveCompany(null);
        setCompanyAddressMissing(null);
        setCompanyFetchError('We could not load your company data. Please check your connection and try again.');
      }
    } finally {
      if (!isBackgroundRetry) {
        setIsLoading(false);
      }
    }
  };

  // The person's companies, for the company switcher. Not on the critical path: if it fails the app keeps working with the current company.
  const loadCompanies = async () => {
    try {
      // lib/authenticated-fetch attaches the session token to every backend request
      const response = await fetch(getApiUrl('api/me/companies'));
      if (!response.ok) return;
      const body = await response.json();
      if (Array.isArray(body?.data)) setCompanies(body.data as CompanyMembership[]);
    } catch {
      setCompanies([]);
    }
  };

  // The kind of account decides the experience. If it cannot be read it stays unresolved (never guessed): the interface then keeps to what every account may see.
  const loadAccountType = async () => {
    try {
      const response = await fetch(getApiUrl('api/me'));
      if (!response.ok) return;
      const body = await response.json();
      const type = body?.data?.account_type;
      setAccountType(type === 'main' || type === 'client_account' || type === 'vendor_account' ? type : null);
      setAccountTypeResolved(true);
    } catch {
      /* stays unresolved */
    }
  };

  const switchCompany = (nextCompanyId: string) => {
    if (!companies.some((c) => c.company_id === nextCompanyId) || nextCompanyId === companyId) return;
    setSelectedCompanyId(nextCompanyId);
    localStorage.removeItem(COMPANY_CACHE_KEY);
    window.location.assign('/');
  };

  const refreshUser = async () => {
    if (user?.id) {
      await fetchUserCompanyRelation(user.id);
    }
  };

  // `user` is no longer local state (it derives from the shared session
  // cache), so this only clears the company/role data that IS still local
  // to this provider.
  const clearUser = () => {
    setCompanyId(null);
    setUserRole(null);
    setIsActiveCompany(null);
    setCompanyAddressMissing(null);
    setCompanyFetchError(null);
    localStorage.removeItem(COMPANY_CACHE_KEY);
  };

  // Enhanced PostHog user identification
  const identifyUserWithPostHog = (user: any, companyData?: any) => {
    const userProperties: any = {
      email: user.email,
      user_id: user.id,
      created_at: user.created_at,
      last_sign_in: user.last_sign_in_at,
      email_confirmed: user.email_confirmed_at ? true : false,
    };

    // Add company and role data if available
    if (companyData) {
      userProperties.company_id = companyData.company_id;
      userProperties.user_role = companyData.role;
      userProperties.is_active_company = companyData.is_active_company;
      userProperties.invited = companyData.invited;
      userProperties.company_relation_id = companyData.id;
    }

    // Add current state
    if (companyId) userProperties.current_company_id = companyId;
    if (userRole) userProperties.current_user_role = userRole;
    if (isActiveCompany !== null) userProperties.current_is_active_company = isActiveCompany;

    posthog.identify(user.id, userProperties);
    
    // Track user login
    posthog.capture('user_logged_in', {
      user_role: companyData?.role || 'unknown',
      company_id: companyData?.company_id || 'unknown',
      is_active_company: companyData?.is_active_company || false,
      timestamp: new Date().toISOString()
    });
  };

  // Root-cause fix (2026-09-20): this used to be its own
  // getSession()/onAuthStateChange pair -- one of three independent
  // subscriptions across the app, each its own path into GoTrueClient's
  // shared auth lock (see lib/auth-session.ts's own docs for the full
  // mechanism, including the exact deadlock this file used to warn about
  // here: an onAuthStateChange callback awaiting another Supabase call).
  // `user` above already derives reactively from the single shared session
  // cache -- this effect only needs to know once the initial seed has
  // settled, so the company-fetch effect below doesn't run against the
  // default `user === null` before that's actually known.
  useEffect(() => {
    let cancelled = false;
    waitForInitialSession().then(() => {
      if (!cancelled) setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetches data that depends on the authenticated user (company/role).
  // Waits for the initial auth check so it doesn't flip isLoading to false
  // before we even know whether there's a session. Also covers a real
  // sign-out (user goes from something to null): resets PostHog and clears
  // whatever company/role data was cached, same as the old
  // onAuthStateChange('SIGNED_OUT') branch did directly.
  useEffect(() => {
    if (!authChecked) return;
    if (user?.id) {
      fetchUserCompanyRelation(user.id);
      // Enhanced PostHog identification will be called after company data is fetched
    } else {
      posthog.reset();
      clearUser();
      setIsLoading(false); // ✅ always clear loading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, user?.id]);


  useEffect(() => {
    if (user?.id) void loadAccountType();
    else { setAccountType(null); setAccountTypeResolved(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && companyId) void loadCompanies();
    else setCompanies([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, companyId]);

  const value: UserContextType = {
    user,
    accountType,
    accountTypeResolved,
    companies,
    switchCompany,
    companyId,
    userRole,
    isActiveCompany,
    companyAddressMissing,
    isLoading,
    companyFetchError,
    refreshUser,
    clearUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
